/**
 * PTO (Paid Time Off) Management Tab - Admin Panel
 * Manages leave requests, balances, and PTO settings
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, 
  Clock,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Settings,
  User,
  Briefcase,
  Heart,
  UserX,
  Gift,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Leave type config
const LEAVE_TYPES = {
  pto: { label: 'PTO', icon: Briefcase, color: 'blue' },
  sick: { label: 'Sick Leave', icon: Heart, color: 'red' },
  personal: { label: 'Personal Day', icon: User, color: 'purple' },
  unpaid: { label: 'Unpaid Leave', icon: UserX, color: 'gray' },
  bereavement: { label: 'Bereavement', icon: Gift, color: 'slate' }
};

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
  red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', border: 'border-red-200 dark:border-red-800' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
  gray: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' },
  slate: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-400', border: 'border-slate-200 dark:border-slate-700' },
  green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', border: 'border-green-200 dark:border-green-800' },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' }
};

export default function PTOTab() {
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests'); // requests, balances, calendar, settings
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth() + 1);
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear());

  const fetchBalances = useCallback(async () => {
    try {
      const response = await api.get('/workforce/pto/balances');
      setBalances(response.data.balances || []);
    } catch (error) {
      console.error('Error fetching balances:', error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const response = await api.get('/workforce/pto/requests');
      setRequests(response.data.requests || []);
      setPendingRequests(response.data.requests?.filter(r => r.status === 'pending') || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      const response = await api.get('/workforce/pto/calendar', { 
        params: { month: calendarMonth, year: calendarYear } 
      });
      setCalendar(response.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  }, [calendarMonth, calendarYear]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/workforce/pto/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      setStaff(response.data.filter(u => u.is_active));
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchBalances(),
      fetchRequests(),
      fetchSettings(),
      fetchStaff()
    ]);
    setLoading(false);
  }, [fetchBalances, fetchRequests, fetchSettings, fetchStaff]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendar();
    }
  }, [activeTab, calendarMonth, calendarYear, fetchCalendar]);

  // Request actions
  const handleApproveRequest = async (requestId) => {
    try {
      const response = await api.post(`/workforce/pto/requests/${requestId}/approve`, {});
      if (response.status === 200) {
        toast.success('Request approved');
        fetchRequests();
        fetchBalances();
      } else {
        toast.error('Failed to approve request');
      }
    } catch (error) {
      toast.error('Error approving request');
    }
  };

  const handleRejectRequest = async (requestId, reason) => {
    try {
      const response = await api.post(`/workforce/pto/requests/${requestId}/reject`, null, {
        params: { reason: reason || '' }
      });
      if (response.status === 200) {
        toast.success('Request rejected');
        fetchRequests();
      } else {
        toast.error('Failed to reject request');
      }
    } catch (error) {
      toast.error('Error rejecting request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!confirm('Cancel this request?')) return;
    try {
      const response = await api.post(`/workforce/pto/requests/${requestId}/cancel`);
      if (response.status === 200) {
        toast.success('Request cancelled');
        fetchRequests();
        fetchBalances();
      }
    } catch (error) {
      toast.error('Error cancelling request');
    }
  };

  // Create request
  const handleCreateRequest = async (requestData) => {
    try {
      const response = await api.post('/workforce/pto/request', requestData);
      if (response.status === 200) {
        toast.success('Request submitted');
        fetchRequests();
        setShowRequestModal(false);
      } else {
        toast.error(response.data.detail || 'Failed to create request');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating request');
    }
  };

  // Adjust balance
  const handleAdjustBalance = async (adjustmentData) => {
    try {
      const response = await api.post('/workforce/pto/balance/adjust', adjustmentData);
      if (response.status === 200) {
        toast.success('Balance adjusted');
        fetchBalances();
        setShowAdjustModal(false);
        setSelectedStaffId(null);
      } else {
        toast.error('Failed to adjust balance');
      }
    } catch (error) {
      toast.error('Error adjusting balance');
    }
  };

  // Save settings
  const handleSaveSettings = async (newSettings) => {
    try {
      const response = await api.put('/workforce/pto/settings', newSettings);
      if (response.status === 200) {
        toast.success('Settings saved');
        fetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    }
  };

  // Calendar navigation
  const navigateMonth = (direction) => {
    let newMonth = calendarMonth + direction;
    let newYear = calendarYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setCalendarMonth(newMonth);
    setCalendarYear(newYear);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Time Off</h2>
          <p className="text-muted-foreground">Manage PTO requests, balances, and leave policies</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setShowRequestModal(true)}
            className="gap-2"
            data-testid="new-pto-request-btn"
          >
            <Plus className="w-4 h-4" />
            New Request
          </Button>
        </div>
      </div>

      {/* Pending Requests Alert */}
      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            <span className="font-medium text-amber-800 dark:text-amber-300">{pendingRequests.length} Pending Request{pendingRequests.length > 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-2">
            {pendingRequests.slice(0, 3).map(request => (
              <div key={request.id} className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{request.staff_name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${COLOR_CLASSES[LEAVE_TYPES[request.leave_type]?.color || 'gray'].bg} ${COLOR_CLASSES[LEAVE_TYPES[request.leave_type]?.color || 'gray'].text}`}>
                    {LEAVE_TYPES[request.leave_type]?.label}
                  </span>
                  <span className="text-xs text-muted-foreground">{request.start_date} - {request.end_date}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" className="h-7" onClick={() => handleApproveRequest(request.id)}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button variant="destructive" size="sm" className="h-7" onClick={() => handleRejectRequest(request.id)}>
                    <XCircle className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border overflow-x-auto pb-px">
        {[
          { id: 'requests', label: 'Requests', icon: Calendar, badge: pendingRequests.length },
          { id: 'balances', label: 'Balances', icon: Clock },
          { id: 'calendar', label: 'Calendar', icon: Calendar },
          { id: 'settings', label: 'Settings', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
              ${activeTab === tab.id 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`pto-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="divide-y divide-border">
            {requests.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No leave requests yet
              </div>
            ) : (
              requests.map(request => {
                const leaveConfig = LEAVE_TYPES[request.leave_type] || LEAVE_TYPES.pto;
                const colorClass = COLOR_CLASSES[leaveConfig.color];
                const Icon = leaveConfig.icon;
                
                return (
                  <div key={request.id} className="p-4 hover:bg-muted/30">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${colorClass.bg} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${colorClass.text}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-foreground">{request.staff_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass.bg} ${colorClass.text}`}>
                              {leaveConfig.label}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)} ({request.days_requested} days)
                          </p>
                          {request.reason && (
                            <p className="text-sm text-muted-foreground mt-1">Reason: {request.reason}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs capitalize
                          ${request.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : ''}
                          ${request.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : ''}
                          ${request.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : ''}
                          ${request.status === 'cancelled' ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400' : ''}`}
                        >
                          {request.status}
                        </span>
                        {request.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7" onClick={() => handleApproveRequest(request.id)}>Approve</Button>
                            <Button variant="destructive" size="sm" className="h-7" onClick={() => handleRejectRequest(request.id)}>Reject</Button>
                          </div>
                        )}
                        {(request.status === 'pending' || request.status === 'approved') && (
                          <Button variant="ghost" size="sm" className="h-7" onClick={() => handleCancelRequest(request.id)}>
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Balances Tab */}
        {activeTab === 'balances' && (
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Staff</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">PTO (Days)</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Sick (Days)</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Personal (Days)</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map(balance => (
                    <tr key={balance.staff_id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-foreground">{balance.staff_name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{balance.staff_role}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-sm font-medium">
                          {balance.pto_days}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm font-medium">
                          {balance.sick_days}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-sm font-medium">
                          {balance.personal_days}
                        </span>
                      </td>
                      <td className="p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setSelectedStaffId(balance.staff_id); setShowAdjustModal(true); }}
                        >
                          <Edit className="w-3 h-3 mr-1" /> Adjust
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && calendar && (
          <div className="p-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="font-medium text-foreground">
                {new Date(calendarYear, calendarMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {(() => {
                const firstDay = new Date(calendarYear, calendarMonth - 1, 1).getDay();
                const daysInMonth = new Date(calendarYear, calendarMonth, 0).getDate();
                const days = [];
                
                // Empty cells before first day
                for (let i = 0; i < firstDay; i++) {
                  days.push(<div key={`empty-${i}`} className="p-2" />);
                }
                
                // Days of month
                for (let day = 1; day <= daysInMonth; day++) {
                  const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const dayLeaves = calendar.calendar[dateStr] || [];
                  const isToday = dateStr === new Date().toISOString().split('T')[0];
                  
                  days.push(
                    <div 
                      key={day}
                      className={`min-h-[80px] p-1 border rounded-lg ${isToday ? 'border-primary bg-primary/5' : 'border-border'}`}
                    >
                      <p className={`text-xs font-medium mb-1 ${isToday ? 'text-primary' : 'text-foreground'}`}>{day}</p>
                      <div className="space-y-0.5">
                        {dayLeaves.slice(0, 3).map((leave, idx) => {
                          const colorClass = COLOR_CLASSES[LEAVE_TYPES[leave.leave_type]?.color || 'gray'];
                          return (
                            <div 
                              key={`${leave.staff_id}-${idx}`}
                              className={`text-[10px] px-1 py-0.5 rounded truncate ${colorClass.bg} ${colorClass.text}`}
                              title={`${leave.staff_name} - ${LEAVE_TYPES[leave.leave_type]?.label}`}
                            >
                              {leave.staff_name}
                            </div>
                          );
                        })}
                        {dayLeaves.length > 3 && (
                          <p className="text-[10px] text-muted-foreground">+{dayLeaves.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  );
                }
                
                return days;
              })()}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && settings && (
          <PTOSettingsPanel settings={settings} onSave={handleSaveSettings} />
        )}
      </div>

      {/* New Request Modal */}
      {showRequestModal && (
        <NewRequestModal
          staff={staff}
          onClose={() => setShowRequestModal(false)}
          onSave={handleCreateRequest}
        />
      )}

      {/* Adjust Balance Modal */}
      {showAdjustModal && selectedStaffId && (
        <AdjustBalanceModal
          staffId={selectedStaffId}
          staff={staff}
          onClose={() => { setShowAdjustModal(false); setSelectedStaffId(null); }}
          onSave={handleAdjustBalance}
        />
      )}
    </div>
  );
}

// New Request Modal
function NewRequestModal({ staff, onClose, onSave }) {
  const [form, setForm] = useState({
    staff_id: '',
    leave_type: 'pto',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    reason: ''
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">New Leave Request</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Staff Member</label>
            <select
              value={form.staff_id}
              onChange={(e) => setForm(prev => ({ ...prev, staff_id: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="">Select staff...</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Leave Type</label>
            <select
              value={form.leave_type}
              onChange={(e) => setForm(prev => ({ ...prev, leave_type: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              {Object.entries(LEAVE_TYPES).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Date</label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Reason (Optional)</label>
            <Input
              value={form.reason}
              onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason for leave..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.staff_id}>Submit Request</Button>
        </div>
      </div>
    </div>
  );
}

// Adjust Balance Modal
function AdjustBalanceModal({ staffId, staff, onClose, onSave }) {
  const staffMember = staff.find(s => s.id === staffId);
  const [form, setForm] = useState({
    staff_id: staffId,
    leave_type: 'pto',
    adjustment_type: 'add',
    hours: 8,
    reason: ''
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Adjust Balance for {staffMember?.name}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Leave Type</label>
            <select
              value={form.leave_type}
              onChange={(e) => setForm(prev => ({ ...prev, leave_type: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="pto">PTO</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal Days</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Adjustment Type</label>
            <select
              value={form.adjustment_type}
              onChange={(e) => setForm(prev => ({ ...prev, adjustment_type: e.target.value }))}
              className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
            >
              <option value="add">Add Hours</option>
              <option value="subtract">Subtract Hours</option>
              <option value="set">Set Balance To</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Hours</label>
            <Input
              type="number"
              min="0"
              step="0.5"
              value={form.hours}
              onChange={(e) => setForm(prev => ({ ...prev, hours: parseFloat(e.target.value) }))}
            />
            <p className="text-xs text-muted-foreground mt-1">{(form.hours / 8).toFixed(2)} days</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Reason</label>
            <Input
              value={form.reason}
              onChange={(e) => setForm(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason for adjustment..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.reason}>Save Adjustment</Button>
        </div>
      </div>
    </div>
  );
}

// PTO Settings Panel
function PTOSettingsPanel({ settings, onSave }) {
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(localSettings);
    setSaving(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Accrual Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="accrual_enabled"
              checked={localSettings.accrual_enabled || false}
              onChange={(e) => handleChange('accrual_enabled', e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="accrual_enabled" className="text-sm text-foreground">Enable PTO accrual</label>
          </div>

          {localSettings.accrual_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Accrual Type</label>
                <select
                  value={localSettings.accrual_type || 'per_hours_worked'}
                  onChange={(e) => handleChange('accrual_type', e.target.value)}
                  className="w-full p-2 rounded-lg border border-border bg-background text-foreground"
                >
                  <option value="per_hours_worked">Per Hours Worked</option>
                  <option value="per_year">Annual Grant</option>
                  <option value="monthly">Monthly Accrual</option>
                </select>
              </div>

              {localSettings.accrual_type === 'per_hours_worked' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">PTO Hours Earned</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={localSettings.accrual_rate || 1}
                      onChange={(e) => handleChange('accrual_rate', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Per Hours Worked</label>
                    <Input
                      type="number"
                      value={localSettings.accrual_unit || 40}
                      onChange={(e) => handleChange('accrual_unit', parseFloat(e.target.value))}
                    />
                  </div>
                </div>
              )}

              {localSettings.accrual_type === 'per_year' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Annual PTO Days</label>
                  <Input
                    type="number"
                    value={localSettings.annual_pto_days || 15}
                    onChange={(e) => handleChange('annual_pto_days', parseFloat(e.target.value))}
                  />
                </div>
              )}

              {localSettings.accrual_type === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Monthly PTO Days</label>
                  <Input
                    type="number"
                    step="0.25"
                    value={localSettings.monthly_pto_days || 1.25}
                    onChange={(e) => handleChange('monthly_pto_days', parseFloat(e.target.value))}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Rollover Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="rollover_enabled"
              checked={localSettings.rollover_enabled || false}
              onChange={(e) => handleChange('rollover_enabled', e.target.checked)}
              className="w-4 h-4 rounded border-border"
            />
            <label htmlFor="rollover_enabled" className="text-sm text-foreground">Allow PTO rollover to next year</label>
          </div>

          {localSettings.rollover_enabled && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Max Rollover Days</label>
              <Input
                type="number"
                value={localSettings.max_rollover_days || 10}
                onChange={(e) => handleChange('max_rollover_days', parseFloat(e.target.value))}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Max PTO Balance (Days)</label>
            <Input
              type="number"
              value={localSettings.max_pto_balance || 30}
              onChange={(e) => handleChange('max_pto_balance', parseFloat(e.target.value))}
            />
            <p className="text-xs text-muted-foreground mt-1">Maximum accrued PTO before cap (0 = no cap)</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-foreground mb-4">Other Leave Types</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Annual Sick Days</label>
            <Input
              type="number"
              value={localSettings.sick_leave_days || 10}
              onChange={(e) => handleChange('sick_leave_days', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Personal Days</label>
            <Input
              type="number"
              value={localSettings.personal_days || 3}
              onChange={(e) => handleChange('personal_days', parseFloat(e.target.value))}
            />
          </div>
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <Button onClick={handleSave} disabled={saving} data-testid="save-pto-settings-btn">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
